import React, { useEffect } from 'react';
import type { BrannanWebsiteHeaderSettings } from '@shared/types/WebsiteHeaderSettings.types';
import { isInIframe } from '@shared/utils/isInIframe';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import type { EventNameNavForStandaloneHeaderProps } from '../HeaderV2/components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import type { EventPageType } from '@graphql/generated-base';
import type { GuestSiteEventDesign } from '../../../layout.types';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { LazyPhotoBackgroundLayout } from '../HeaderV2/layouts/PhotoBackground.lazy';
import { LazyPhotoBackgroundMonogramLayout } from '../HeaderV2/layouts/PhotoBackgroundMonogram.lazy';
import { LazyVideoBackgroundLayout } from '../HeaderV2/layouts/VideoBackground.lazy';
import { LazyVideoBackgroundMonogramLayout } from '../HeaderV2/layouts/VideoBackgroundMonogram.lazy';
import { LazyVideoBackgroundHotfixLayout } from '../HeaderV2/layouts/VideoBackgroundHotfix.lazy';
import { isConvertedMotionHeader } from '../HeaderV2/components/HeaderMotion/multiPhotoMotionHeader';
import { useEventInfo } from '@shared/utils/eventInfo';
import { isStandaloneHeroLayout } from '@shared/utils/standaloneHeroLayout';
import { useApplicatorSpriteGuard } from './useApplicatorSpriteGuard';
import { useIsCompactBrannanHeader } from '../HeaderV2/hooks/useIsCompactBrannanHeader';
import { Box } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';

type StandaloneWebsiteHeaderProps = Readonly<EventNameNavForStandaloneHeaderProps> &
  Readonly<{
    websiteHeader: NonNullable<BrannanWebsiteHeaderSettings['websiteHeader']>;
    currentPageType: EventPageType | undefined;
    eventDesign: GuestSiteEventDesign;
  }>;

type VideoBackgroundPayload = Readonly<{
  [eventId: string]: {
    videoId?: string;
    mobileVideoId?: string;
    fallbackImageUrl?: string;
  };
}>;

const isPreview = isInIframe();

export const StandaloneWebsiteHeader = (props: StandaloneWebsiteHeaderProps) => {
  const { websiteHeader, ...restProps } = props;
  const { eventInfo } = useEventInfo();
  const [isVideoBackgroundEnabled, , , payload] = useIsFeatureEnabled('websiteVideoBackgroundEnabled');
  const videoBackgroundPayload = eventInfo?.eventId && payload ? (payload as VideoBackgroundPayload)[eventInfo.eventId] : null;

  const headerPhoto = websiteHeader.headerPhoto;
  const showPlaceholder = isPreview && !!headerPhoto.presentationLayout;

  useApplicatorSpriteGuard(isStandaloneHeroLayout(headerPhoto.presentationLayout));
  const { isCompactView } = useIsCompactBrannanHeader({
    pages: restProps.pages,
    pageSlug: restProps.pageSlug,
    currentPageType: restProps.currentPageType,
    brannanNavigation: restProps.eventDesign?.brannanNavigation,
  });

  useEffect(() => {
    if (headerPhoto.presentationLayout) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [headerPhoto.presentationLayout]);

  // A Slider/Marquee that would become a Showcase header, while the Motion flag is still loading: hold an
  // empty hero of the header's height rather than show the strip and then swap it, or play the opening
  // where nobody sees it. Server-rendered variants usually settle the flag before the first paint.
  if ('isMotionPending' in headerPhoto && headerPhoto.isMotionPending) {
    // The hero's own heights (PhotoBackground): 90svh below `sm`, 100svh above, and 96px in compact view.
    return (
      <Box
        data-hm-pending=""
        aria-hidden="true"
        height={isCompactView ? pxToRem(96) : { _: '90svh', sm: '100svh' }}
      />
    );
  }

  switch (headerPhoto.presentationLayout) {
    case 'photoBackground':
      // If video background is enabled and there is a video background payload, render the video background layout
      // Otherwise, render the photo background layout
      // We recently changed the feature flag to be enabled by default,
      // so we need to check if the video background payload is present
      // The legacy override replaces saved photo headers only: a converted Showcase header must reach
      // PhotoBackgroundLayout, which mounts its Motion.
      return isVideoBackgroundEnabled && videoBackgroundPayload && !isConvertedMotionHeader(headerPhoto) ? (
        <LazyVideoBackgroundHotfixLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          {...restProps}
          videoId={videoBackgroundPayload?.videoId || ''}
          mobileVideoId={videoBackgroundPayload.mobileVideoId}
          fallbackImageUrl={videoBackgroundPayload?.fallbackImageUrl || ''}
        />
      ) : (
        <LazyPhotoBackgroundLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          {...restProps}
        />
      );
    case 'photoBackgroundMonogram':
      return (
        <LazyPhotoBackgroundMonogramLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          {...restProps}
        />
      );
    case 'videoBackground':
      return (
        <LazyVideoBackgroundLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );
    case 'videoBackgroundMonogram':
      return (
        <LazyVideoBackgroundMonogramLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );
    default:
      return null;
  }
};

StandaloneWebsiteHeader.displayName = 'StandaloneWebsiteHeader';
